import FetchSubject from '@Application/Subjects/Queries/FetchSubject';
import fetchSubject from '@Application/Subjects/fetchSubject';
import FetchSubjectMembers from '@Application/Subjects/Members/Queries/FetchSubjectMembers';
import FetchSubjectUsers from '@Application/Subjects/Members/Queries/FetchSubjectUsers';
import fetchSubjectMembers from '@Application/Subjects/Members/fetchSubjectMembers';
import fetchSubjectUsers from '@Application/Subjects/Members/fetchSubjectUsers';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import PeopleList from '@UI/Pages/Beneficiaries/Shared/SubjectScreen/PeopleList';
import PeoplePanel from '@UI/Pages/Beneficiaries/Shared/SubjectScreen/PeoplePanel';
import CandidateRow from '@UI/Pages/Subjects/SubjectDetails/Pages/Members/CandidateRow';
import MemberRow from '@UI/Pages/Subjects/SubjectDetails/Pages/Members/MemberRow';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import { Users } from 'lucide-react';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Skeleton } from '@verclam/ui-kit';

type Props = {
	readonly subject: SubjectAddress;
};

/**
 * Owns the fetch and every loading/error/empty/nominal branch of the members screen.
 *
 * Members, the users of the dossier and the dossier itself are three independent reads: the member
 * names are resolved by joining the members' `{ id }` against the users, and the sharing gestures
 * exist only when the dossier says `canShare`. A mutation answers `204`, so its application function
 * reloads members and users; a member who lowers their own access also reloads the dossier, whose
 * `canWrite` and `canShare` have just changed.
 *
 * `error !== null` comes before the data-absent branch, never the reverse (`guides/state-stores.md`).
 */
const MembersContent: React.FC<Props> = ({ subject }) => {
	const subjectId = subject.subjectId;
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;

	const {
		data: loadedSubject,
		error: subjectError,
		refetch: refetchSubject,
	} = useStoreQuery({
		selector: (state) => state.subjectsStore.subject,
		stopOn: (value) => value !== null && value.id === subjectId,
		query: () => dispatch(fetchSubject(new FetchSubject(facilityId, subjectId))),
		deps: [subjectId, facilityId],
	});

	const {
		data: members,
		error: membersError,
		refetch: refetchMembers,
	} = useStoreQuery({
		selector: (state) => state.subjectsStore.members,
		stopOn: (value) => value?.isFor(subject) === true,
		query: () => dispatch(fetchSubjectMembers(new FetchSubjectMembers(facilityId, subject))),
		deps: [facilityId, subject],
	});

	const {
		data: users,
		error: usersError,
		refetch: refetchUsers,
	} = useStoreQuery({
		selector: (state) => state.subjectsStore.users,
		stopOn: (value) => value?.isFor(subject) === true,
		query: () => dispatch(fetchSubjectUsers(new FetchSubjectUsers(facilityId, subject))),
		deps: [facilityId, subject],
	});

	const error = subjectError ?? membersError ?? usersError;

	const handleRetry = React.useCallback(() => {
		refetchSubject();
		refetchMembers();
		refetchUsers();
	}, [refetchSubject, refetchMembers, refetchUsers]);

	const handleOwnAccessLowered = React.useCallback(() => {
		refetchSubject();
	}, [refetchSubject]);

	const candidates = React.useMemo(
		() => (members === null || users === null ? [] : users.candidates(members)),
		[members, users],
	);

	if (error !== null) {
		return <ErrorState title="Impossible de charger les membres" onRetry={handleRetry} />;
	}

	const currentSubject = loadedSubject !== null && loadedSubject.id === subjectId ? loadedSubject : null;

	if (currentSubject === null || members === null || users === null) {
		return <Skeleton height="14rem" animation="wave" shape="rectangle" />;
	}

	const canShare = currentSubject.canManageMembers;

	return (
		<React.Fragment>
			<PeoplePanel title={`Membres · ${members.members.length}`}>
				<PeopleList>
					{members.members.map((member) => (
						<MemberRow
							key={member.userId}
							member={member}
							user={users.findById(member.userId)}
							subject={subject}
							facilityId={facilityId}
							canShare={canShare}
							onOwnAccessLowered={handleOwnAccessLowered}
						/>
					))}
				</PeopleList>
			</PeoplePanel>

			<ConditionalLoader data={canShare}>
				<PeoplePanel title="Personnes de l'agence">
					{candidates.length === 0 ? (
						<EmptyState
							title="Aucune autre personne de l'agence n'a accès à ce type de dossier."
							icon={<Users size={28} />}
						/>
					) : (
						<PeopleList>
							{candidates.map((candidate) => (
								<CandidateRow
									key={candidate.id}
									candidate={candidate}
									subject={subject}
									facilityId={facilityId}
								/>
							))}
						</PeopleList>
					)}
				</PeoplePanel>
			</ConditionalLoader>
		</React.Fragment>
	);
};

export default MembersContent;
